import React, { useRef, useCallback, useEffect, memo, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import BodyCell from "../cells/BodyCell";
import DatePickerCell from "../cells/DatePickerCell";
import TextCell from "../cells/TextCell";
import NoteCell from "../cells/NoteCell";
import TextCellLink from "../cells/TextCellLink";
import NameCell from "../cells/NameCell";

import BoxNumberCell from "../cells/BoxNumberCell";
import RelatedPatientsBox from "../cells/RelatedPatientsBox";
import AttyStatusBox from "../cells/AttyStatusBox";
import SourceBox from "../cells/SourceBox";
import PhoneBox from "../cells/PhoneBox";
import IconsCell from "../cells/IconsCell";

import classes from "./WorkQRow.module.css";
import tableClasses from "./WorkQComponent.module.css";
import ObjectCell from "../cells/ObjectCell";
import CurencyCell from "../cells/CurencyCell";
import PercentCell from "../cells/PercentCell";
import StatusCell from "../cells/StatusCell";
import LimitsCell from "../cells/LimitsCell";
import MultipleOptionsCell from "../cells/MultipleOptionsCell";
import ProviderCell from "../cells/ProviderCell";
import CheckCell from "../cells/CheckCell";
import { caseQuickUpdate, workQQuickUpdate } from "../../../lib/fetchApi";
import { useDoNotLienConfirm } from "../../patient/details/lien/doNotLienConfirm";
import { notificationActions } from "../../../store/notifications";
import TextCellCopy from "../cells/TextCellCopy";
import TasksCell from "../cells/TasksCell";
import AlertCell from "../cells/AlertCell";
import { filterActions } from "../../../store/filter";
import VerificationCell from "../cells/VerificationCell";
import WorkQDrawerWrapper from "./drawer/WorkQDrawerWrapper";
import AICADepartmentsCell from "../cells/AICADepartmentsCell";
import MultipleRowsCell from "../cells/MultipleRowsCell";
import GDQStatusCell from "../cells/GDQStatusCell";
import MrqNotesCell from "../cells/MrqNotesCell";
import { filterWorkQPracticeRowsForDisplay } from "../../../lib/workQPracticeDisplayFilter";
import { isLienRelatedWorkQ } from "../../../lib/systemData";
import MrqCoverLetterCell from "../cells/MrqCoverLetterCell";
import AuditCompleteCell from "../cells/AuditCompleteCell";
import WorkQRowTrWrapper from "./WorkQRowTrWrapper";
import ProcodocsNotifications from "../cells/ProcodocsNotifications";
import NegStatusCell from "../cells/NegStatusCell";
import { getCaseIdFromCasePayload } from "../../../lib/caseUpdateHelpers";

const FU_COMMIT_DEBOUNCE_MS = 400;

function workQColumnVisibleInTable(item) {
    if (item?.show_in_table === undefined || item?.show_in_table === null) {
        return true;
    }
    return item.show_in_table === 1 || item.show_in_table === true || item.show_in_table === "1";
}

function WorkQRow({ data, index, slug, workq, deferRowPaint, groupedFields }) {
    const dispatch = useDispatch();

    const filteredData = groupedFields;

    const activeCaseId = useSelector((state) => {
        const ac = state.case.activeCase;
        return ac ? getCaseIdFromCasePayload(ac) : null;
    });
    const userToken = useSelector((state) => state.user.token);
    const filters = useSelector((state) => state.filter.filters);

    const displayPracticeGroups = useMemo(() => {
        if (!isLienRelatedWorkQ(workq)) {
            return {
                practices: data?.practices,
                liens: data?.liens,
            }
        }
        return filterWorkQPracticeRowsForDisplay(
            data?.practices,
            data?.liens,
            filters,
            workq?.filter_conditions,
        )
    }, [data?.practices, data?.liens, filters, workq]);

    const rowDataRef = useRef(data);
    rowDataRef.current = data;

    const quickUpdateSequenceByKeyRef = useRef(new Map());

    const nextSequenceForKey = (key) => {
        const map = quickUpdateSequenceByKeyRef.current;
        const next = (map.get(key) ?? 0) + 1;
        map.set(key, next);
        return next;
    };

    const { confirm: confirmDoNotLien, modal: doNotLienConfirmModal } = useDoNotLienConfirm();

    const updateHandler = useCallback(
        async (payload) => {
            const rowData = rowDataRef.current;

            if (payload.identifier.field === 'do_not_lien') {
                const nextValue = payload.data.value ? 1 : 0
                const confirmed = await confirmDoNotLien({ engaging: nextValue === 1 })
                if (!confirmed) {
                    return
                }
                payload.data.value = nextValue
            }

            const reqBody = {
                type: payload.identifier.type,
                type_id: payload.identifier.type_id,
                workq: slug,
                fields: {
                    [payload.identifier.field]: payload.data.value,
                },
            };

            const staleGuardKey = `${rowData.case.id}:${payload.identifier.type}:${payload.identifier.type_id}:${payload.identifier.field}`;
            const staleGuardSequence = nextSequenceForKey(staleGuardKey);
            const update = await caseQuickUpdate(userToken, rowData.case.id, reqBody, null, true, {
                staleGuardKey,
                staleGuardSequence,
            });

            if (update?.staleResponse) {
                return;
            }

            if (update && update.errorMessage) {
                dispatch(notificationActions.setError(update));
            } else {
                dispatch(
                    notificationActions.setError({
                        errorMessage: `Your changes have been successfully saved to the case.`,
                        type: "success",
                    }),
                );
            }
        },
        [dispatch, userToken, slug, confirmDoNotLien],
    );

    const fuDebounceRef = useRef(null);
    const latestFuRef = useRef(null);

    useEffect(
        () => () => {
            if (fuDebounceRef.current) {
                clearTimeout(fuDebounceRef.current);
            }
        },
        [],
    );

    const nextFollowUpDateUpdateHandler = useCallback(
        (payload) => {
            const rowData = rowDataRef.current;
            const previousValue = rowData?.[payload?.identifier?.folder]?.[payload?.identifier?.field] ?? null;
            const nextValue = payload?.data?.value ?? null;

            const reqBody = {
                [payload.identifier.field]: nextValue,
            };

            const optimisticDispatchBody = {
                case_id: payload.identifier.case_id,
                folder: payload.identifier.folder,
                item: {
                    itemId: payload.identifier.type_id,
                    fields: [
                        {
                            field: payload.identifier.field,
                            value: nextValue,
                        },
                    ],
                },
            };
            dispatch(filterActions.updateItemInWorkQRow(optimisticDispatchBody));

            const staleGuardKey = `${slug}:${rowData.case.id}:${payload.identifier.field}`;
            latestFuRef.current = {
                payload,
                previousValue,
                optimisticDispatchBody,
                staleGuardKey,
                reqBody,
            };

            if (fuDebounceRef.current) {
                clearTimeout(fuDebounceRef.current);
            }

            fuDebounceRef.current = setTimeout(async () => {
                fuDebounceRef.current = null;
                const pack = latestFuRef.current;
                if (!pack) return;

                const staleGuardSequence = nextSequenceForKey(pack.staleGuardKey);
                const rowDataNow = rowDataRef.current;
                const update = await workQQuickUpdate(userToken, slug, rowDataNow.case.id, pack.reqBody, null, {
                    staleGuardKey: pack.staleGuardKey,
                    staleGuardSequence,
                });

                if (update?.staleResponse) {
                    return;
                }

                if (update?.data && update.data.errorMessage) {
                    dispatch(notificationActions.setError(update.data));
                    dispatch(
                        filterActions.updateItemInWorkQRow({
                            ...pack.optimisticDispatchBody,
                            item: {
                                ...pack.optimisticDispatchBody.item,
                                fields: [
                                    {
                                        field: pack.payload.identifier.field,
                                        value: pack.previousValue,
                                    },
                                ],
                            },
                        }),
                    );
                } else {
                    dispatch(
                        notificationActions.setError({
                            errorMessage: `Your changes have been successfully saved to the case.`,
                            type: "success",
                        }),
                    );

                    const dispatchBody = {
                        case_id: pack.payload.identifier.case_id,
                        folder: pack.payload.identifier.folder,
                        item: {
                            itemId: pack.payload.identifier.type_id,
                            fields: [
                                {
                                    field: pack.payload.identifier.field,
                                    value: pack.payload.data.value,
                                },
                            ],
                        },
                    };

                    dispatch(filterActions.updateItemInWorkQRow(dispatchBody));
                }
            }, FU_COMMIT_DEBOUNCE_MS);
        },
        [dispatch, userToken, slug],
    );

    return (
        <>
            <WorkQRowTrWrapper index={index} activeCaseId={activeCaseId} data={data} classes={classes} workq={workq} deferRowPaint={deferRowPaint}>
                {filteredData &&
                    filteredData.map((item, itemIndex) => {
                        const visibleItems = item.items
                            .filter((elem) => elem.type !== "hover" && workQColumnVisibleInTable(elem));
                        return (
                        <React.Fragment key={`${item.groupName ?? "group"}_${itemIndex}`}>
                            {visibleItems.map((sub_item, sub_item_index) => {
                                    const cellKey = `${data.case.id}-${sub_item.key ?? sub_item.name ?? "col"}-${sub_item_index}`;
                                    return (
                                        <BodyCell
                                            key={cellKey}
                                            data={item}
                                            classStyle={`
                        ${sub_item.style && sub_item.style == "full" ? "" : "table-cell-fit "} 
                        ${sub_item.isSticky ? "table-cell-sticky" : ""}
                         ${sub_item_index == visibleItems.length - 1 ? "table-cell-last" : ""}
                         ${sub_item.type === "neg_status" ? `${tableClasses.td_cell_no_padding} ${tableClasses.td_cell_height_100}` : ""}`}
                                            data_atr={`index_${itemIndex}_${sub_item_index}`}
                                        >
                                            {sub_item.type == "mrq_cover_letter" && (
                                                <MrqCoverLetterCell caseData={data} />
                                            )}
                                            {sub_item.type == "audit_complete" && (
                                                <AuditCompleteCell caseData={data} />
                                            )}

                                            {sub_item.group && sub_item.label && sub_item.type !== "audit_complete" && (
                                                <>
                                                    {(sub_item.type == "date" || sub_item.type == "datetime") && (
                                                        <DatePickerCell
                                                            date={data[sub_item.group] ? data[sub_item.group][sub_item.label] : null}
                                                            identifier={{
                                                                case_id: data?.case?.id,
                                                                folder: sub_item.group,
                                                                type: sub_item.group,
                                                                field: sub_item.label,
                                                            }}
                                                            disabled={true}
                                                            item={data}
                                                            action={nextFollowUpDateUpdateHandler}
                                                            workq={workq}
                                                            isDateTime={sub_item.type == "datetime"}
                                                        />
                                                    )}

                                                    {sub_item.type == "name" && (
                                                        <NameCell
                                                            text={data[sub_item.group] ? data[sub_item.group][sub_item.label] : null}
                                                            special={sub_item.special}
                                                            group={data[sub_item.group]}
                                                            item={data}
                                                            slug={slug}
                                                        />
                                                    )}
                                                    {sub_item?.type == "text" && (
                                                        <TextCell
                                                            text={data[sub_item.group] ? data[sub_item.group][sub_item.label] : null}
                                                            group={data[sub_item.group]}
                                                            itemKey={sub_item.key}
                                                            caseData={data}
                                                        />
                                                    )}
                                                    {sub_item?.type == "text_copy" && (
                                                        <TextCellCopy text={data[sub_item.group] ? data[sub_item.group][sub_item.label] : null} item={data} />
                                                    )}
                                                    {sub_item.type == "object" && (
                                                        <ObjectCell data={data[sub_item.group] ? data[sub_item.group][sub_item.label] : null} label={sub_item.label} />
                                                    )}

                                                    {sub_item.type == "curency" && (
                                                        <CurencyCell text={data[sub_item.group] ? data[sub_item.group][sub_item.label] : null} test={sub_item} />
                                                    )}
                                                    {sub_item.type == "currency" && (
                                                        <CurencyCell
                                                            text={data[sub_item.group] ? data[sub_item.group][sub_item.label] : null}
                                                            test={sub_item}
                                                            itemKey={sub_item.key}
                                                            caseData={data}
                                                        />
                                                    )}

                                                    {sub_item.type == "percent" && (
                                                        <PercentCell text={data[sub_item.group] ? data[sub_item.group][sub_item.label] : null} />
                                                    )}

                                                    {sub_item.type == "add_notes" && <NoteCell caseData={data} notes={data[sub_item.group]} workq={workq} />}

                                                    {sub_item.type == "mrq_notes" && (
                                                        <MrqNotesCell
                                                            note={data[sub_item.group] ? data[sub_item.group][sub_item.label] : null}
                                                            caseData={data}
                                                            workq={workq}
                                                        />
                                                    )}

                                                    {sub_item.type == "add_tasks" && <TasksCell caseData={data} notes={data[sub_item.group]} workq={workq} />}

                                                    {sub_item.type == "add_alerts" && <AlertCell caseData={data} notes={data[sub_item.group]} workq={workq} />}

                                                    {sub_item.type == "checkbox" && (
                                                        <CheckCell checked={data[sub_item.group] ? data[sub_item.group][sub_item.label] : null} isDisabled={true} />
                                                    )}
                                                    {sub_item.type == "box" && (
                                                        <BoxNumberCell text={data[sub_item.group] ? data[sub_item.group][sub_item.label] : null} />
                                                    )}
                                                    {sub_item.type == "icons" && (
                                                        <IconsCell data={data[sub_item.group] ? data[sub_item.group][sub_item.label] : null} item={item} />
                                                    )}
                                                    {sub_item.type == "related_patients" && (
                                                        <RelatedPatientsBox data={data[sub_item.group] ? data[sub_item.group][sub_item.label] : null} />
                                                    )}
                                                    {sub_item.type == "status" && (
                                                        <StatusCell
                                                            data={data[sub_item.group] ? data[sub_item.group][sub_item.label] : null}
                                                            group={data[sub_item.group]}
                                                            itemKey={sub_item.key}
                                                            caseData={data}
                                                            itemData={sub_item}
                                                            workqSlug={slug}
                                                        />
                                                    )}
                                                    {sub_item.type == "limits" && (
                                                        <LimitsCell data={data[sub_item.group] ? data[sub_item.group][sub_item.label] : null} itemKey={sub_item.key} caseData={data} />
                                                    )}
                                                    {sub_item.type == "multiple_options" && (
                                                        <MultipleOptionsCell data={data[sub_item.group] ? data[sub_item.group][sub_item.label] : null} itemKey={sub_item.key} />
                                                    )}
                                                    {sub_item.type == "provider" && (
                                                        <ProviderCell data={data[sub_item.group] ? data[sub_item.group][sub_item.label] : null} itemKey={sub_item.key} caseData={data} />
                                                    )}

                                                    {sub_item.type == "atty_status" && (
                                                        <AttyStatusBox data={data[sub_item.group] ? data[sub_item.group][sub_item.label] : null} group={data[sub_item.group]} />
                                                    )}
                                                    {sub_item.type == "source_id" && (
                                                        <SourceBox data={data[sub_item.group] ? data[sub_item.group][sub_item.label] : null} />
                                                    )}
                                                    {sub_item.type == "phone" && (
                                                        <PhoneBox data={data[sub_item.group] ? data[sub_item.group][sub_item.label] : null} group={data[sub_item.group]} />
                                                    )}
                                                    {sub_item.type == "text_link" && (
                                                        <TextCellLink
                                                            text={data[sub_item.group] ? data[sub_item.group][sub_item.label] : null}
                                                            special={sub_item.special}
                                                            group={data[sub_item.group]}
                                                            item={data}
                                                        />
                                                    )}

                                                    {sub_item.type == "number" && (
                                                        <BoxNumberCell text={data[sub_item.group] ? data[sub_item.group][sub_item.label] : null} />
                                                    )}
                                                    {sub_item.type == "boolean" && (
                                                        <CheckCell
                                                            checked={data[sub_item.group] ? data[sub_item.group][sub_item.label] : null}
                                                            isDisabled={!sub_item.editable}
                                                            action={updateHandler}
                                                            itemInfo={sub_item}
                                                            caseData={data}
                                                        />
                                                    )}

                                                    {sub_item.type == "verification" && <VerificationCell item={data} sub_item={sub_item} workqSlug={slug} />}

                                                    {sub_item.type == "aica_department_status" && (
                                                        <AICADepartmentsCell
                                                            classes={classes}
                                                            specialityData={data[sub_item.group]}
                                                            prefix={sub_item?.name?.toLowerCase()}
                                                            caseData={data}
                                                            workqSlug={slug}
                                                        />
                                                    )}
                                                    {sub_item.type == "aica_department_status_update" && (
                                                        <AICADepartmentsCell
                                                            classes={classes}
                                                            specialityData={data[sub_item.group]}
                                                            prefix={sub_item?.name?.toLowerCase()}
                                                            caseData={data}
                                                            editable={true}
                                                            workqSlug={slug}
                                                        />
                                                    )}
                                                    {sub_item.type == "table" && (
                                                        <MultipleRowsCell
                                                            data={
                                                                sub_item.group === "practices" || sub_item.group === "liens"
                                                                    ? displayPracticeGroups[sub_item.group]
                                                                    : data[sub_item.group]
                                                            }
                                                            sub_item={sub_item}
                                                            caseData={data}
                                                            slug={slug}
                                                        />
                                                    )}

                                                    {sub_item.type == "gdq_status" && (
                                                        <GDQStatusCell data={data[sub_item.group] ? data[sub_item.group][sub_item.label] : null} item={data} caseData={data} />
                                                    )}

                                                    {sub_item.type == "neg_status" && (
                                                        <NegStatusCell status={data[sub_item.group]?.[sub_item.label]} />
                                                    )}

                                                    {sub_item.type == "procodocs_notifications" && (
                                                        <ProcodocsNotifications
                                                            data={data[sub_item.group] ? data[sub_item.group][sub_item.label] : null}
                                                            item={data}
                                                            caseData={data}
                                                            slug={slug}
                                                        />
                                                    )}
                                                </>
                                            )}
                                        </BodyCell>
                                    );
                                })}
                        </React.Fragment>
                        );
                    })}
            </WorkQRowTrWrapper>

            <WorkQDrawerWrapper data={data} slug={slug} workq={workq} />
            {doNotLienConfirmModal}
        </>
    );
}

export default memo(WorkQRow);
